import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    fetchInvoiceById
} from "../../services/api/billing/invoiceService";
import {
    getPaymentsByInvoice,
    downloadPayment
} from "../../services/api/billing/paymentService";
import { Download } from "lucide-react";

const InvoiceView = () => {
    const { id } = useParams();

    const [invoice, setInvoice] = useState(null);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        setLoading(true);
        try {
            const inv = await fetchInvoiceById(id);
            setInvoice(inv);

            const pay = await getPaymentsByInvoice(id);
            setPayments(pay.data || []);
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    };

    const handlePaymentDownload = async (pid) => {
        try {
            const res = await downloadPayment(pid);
            const blob = new Blob([res.data]);
            const url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `payment_${pid}.pdf`;
            link.click();

            URL.revokeObjectURL(url);
        } catch {
            alert("Download failed");
        }
    };

    if (loading) return <p className="p-4">Loading...</p>;
    if (!invoice) return <p className="p-4">Invoice not found</p>;

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen">

            {/* HEADER */}
            <div className="mb-5">
                <h2 className="text-2xl font-bold">
                    Invoice #{invoice.invoice_no}
                </h2>
                <p className="text-gray-500">{invoice.invoice_date}</p>
            </div>

            {/* INFO */}
            <div className="grid md:grid-cols-3 gap-4 mb-6">

                <div className="bg-white p-4 rounded shadow">
                    <p className="text-gray-500 text-sm">Status</p>
                    <span className={`px-2 py-1 rounded text-xs
                        ${invoice.status === "paid"
                            ? "bg-green-100 text-green-700"
                            : "bg-yellow-100 text-yellow-700"}
                    `}>
                        {invoice.status}
                    </span>
                </div>

                <div className="bg-white p-4 rounded shadow">
                    <p className="text-gray-500 text-sm">DC Out ID</p>
                    <p className="font-semibold">{invoice.dc_out_id || "-"}</p>
                </div>

                <div className="bg-white p-4 rounded shadow">
                    <p className="text-gray-500 text-sm">Total</p>
                    <p className="font-bold text-lg">
                        ₹ {Number(invoice.total).toFixed(2)}
                    </p>
                </div>

            </div>

            {/* ITEMS */}
            <div className="bg-white p-4 rounded shadow mb-6 overflow-x-auto">
                <h3 className="font-semibold mb-3">Items</h3>

                <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="p-2 text-left">Item</th>
                            <th className="p-2">Qty</th>
                            <th className="p-2">Price</th>
                            <th className="p-2">Amount</th>
                        </tr>
                    </thead>

                    <tbody>
                        {invoice.items?.map((item, i) => (
                            <tr key={i} className="border-t">
                                <td className="p-2">{item.item_name}</td>
                                <td className="p-2 text-center">{item.qty}</td>
                                <td className="p-2 text-center">
                                    ₹ {Number(item.price).toFixed(2)}
                                </td>
                                <td className="p-2 text-center">
                                    ₹ {Number(item.amount).toFixed(2)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* TOTAL */}
            <div className="bg-white p-4 rounded shadow mb-6 text-right text-sm">
                <p>Subtotal: ₹ {Number(invoice.subtotal).toFixed(2)}</p>
                <p>CGST: ₹ {Number(invoice.cgst).toFixed(2)}</p>
                <p>SGST: ₹ {Number(invoice.sgst).toFixed(2)}</p>
                <p>IGST: ₹ {Number(invoice.igst).toFixed(2)}</p>

                <p className="font-bold text-lg mt-2">
                    Total: ₹ {Number(invoice.total).toFixed(2)}
                </p>
            </div>

            {/* PAYMENTS */}
            <div className="bg-white p-4 rounded shadow">

                <h3 className="font-semibold mb-3">Payments</h3>

                {/* DESKTOP */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr className="text-left">
                                <th className="p-2">ID</th>
                                <th className="p-2">Amount</th>
                                <th className="p-2">Date</th>
                                <th className="p-2">Mode</th>
                                <th className="p-2">Txn Ref</th>
                                <th className="p-2">Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {payments.length > 0 ? payments.map((p, index) => (
                                <tr key={p.id} className="border-t">
                                    <td className="p-2">{index+1}</td>
                                    <td className="p-2">₹ {p.amount}</td>
                                    <td className="p-2">{p.payment_date}</td>
                                    <td className="p-2">{p.mode}</td>
                                    <td className="p-2">{p.txn_ref_no || "-"}</td>
                                    <td className="p-2 text-center">
                                        <button onClick={() => handlePaymentDownload(p.id)}>
                                            <Download size={14} />
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="6" className="text-center p-3">
                                        No payments found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* MOBILE */}
                <div className="md:hidden space-y-2">
                    {payments.map(p => (
                        <div key={p.id} className="bg-gray-50 p-3 rounded text-sm">
                            <div className="flex justify-between">
                                <span className="font-semibold">₹ {p.amount}</span>
                                <span>{p.mode}</span>
                            </div>
                            <p className="text-gray-500">{p.payment_date}</p>
                            <p className="text-gray-600 text-xs">
                                Txn: {p.txn_ref_no || "-"}
                            </p>

                            <div className="flex justify-end mt-2">
                                <button onClick={() => handlePaymentDownload(p.id)}>
                                    <Download size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

            </div>

        </div>
    );
};

export default InvoiceView;